import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import VideoCreativeService from 'services/VideoCreativeService';
import { ScriptDelivery } from './types';

const service = new VideoCreativeService();

interface Props {
  open: boolean;
  onClose: () => void;
  storyboardId: string | null;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// Pop-up listing every time a script was sent to a creator — when, to whom, the
// title used, the product, and the Trello / Drive / Notion links. Opened by
// clicking a script's "Delivered (N)" badge.
export default function DeliveriesDialog({ open, onClose, storyboardId }: Props) {
  const [deliveries, setDeliveries] = useState<ScriptDelivery[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !storyboardId) return;
    setLoading(true);
    setError(null);
    setDeliveries([]);
    service.listStoryboardDeliveries(storyboardId).then((res) => {
      if (res) setDeliveries(res.deliveries);
      else setError('Failed to load deliveries.');
      setLoading(false);
    });
  }, [open, storyboardId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Deliveries{!loading && deliveries.length ? ` (${deliveries.length})` : ''}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : deliveries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No deliveries recorded for this script.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sent</TableCell>
                  <TableCell>Creator</TableCell>
                  <TableCell>Title used</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Links</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveries.map((d) => (
                  <TableRow key={d.deliveryId} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatWhen(d.createdAt)}</TableCell>
                    <TableCell>{d.creatorName || '—'}</TableCell>
                    <TableCell>{d.scriptName}</TableCell>
                    <TableCell>{d.productName || '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5}>
                        {d.trelloCardUrl
                          ? <Link href={d.trelloCardUrl} target="_blank" rel="noopener noreferrer">Trello</Link>
                          : <Typography variant="caption" color="text.disabled">Trello</Typography>}
                        {d.driveFolderUrl
                          ? <Link href={d.driveFolderUrl} target="_blank" rel="noopener noreferrer">Drive</Link>
                          : <Typography variant="caption" color="text.disabled">Drive</Typography>}
                        {d.notionPageUrl
                          ? <Link href={d.notionPageUrl} target="_blank" rel="noopener noreferrer">Notion</Link>
                          : <Typography variant="caption" color="text.disabled">Notion</Typography>}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
